import { useEffect, useRef, useState } from 'react';
import { updateUserPnl } from '../../services/UsersService';

function apiError(err) {
    const raw = err?.response ? err.response.data : err?.message;
    if (typeof raw === 'string') return raw;
    if (raw && raw.message) return raw.message;
    return raw ? JSON.stringify(raw) : 'Não foi possível corrigir o PnL.';
}

function toInput(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return String(Number(number.toFixed(2)));
}

function formatUsd(value, signed = false) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '0,00';
    const formatted = number.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    if (!signed) return formatted;
    if (number > 0) return `+${formatted}`;
    return formatted;
}

function PnlModal({ data, onSubmit }) {
    const closeRef = useRef(null);
    const [pnl, setPnl] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const userId = data?.user?.id || data?.id;
    const walletTotal = Number(data?.walletTotal);
    const currentPnl = Number(data?.pnl);
    const deposited = Number(data?.depositedUsdc ?? data?.allocation);

    useEffect(() => {
        setPnl(toInput(data?.pnl));
        setError('');
    }, [userId, data?.pnl, data?.depositedUsdc, data?.walletTotal]);

    const parsed = parseFloat(pnl);
    const previewDeposited = Number.isFinite(parsed) && Number.isFinite(walletTotal)
        ? Math.max(0, walletTotal - parsed)
        : null;

    function onSave() {
        if (!userId) return;
        if (!Number.isFinite(parsed)) {
            setError('Informe o PnL correto em USDC.');
            return;
        }
        if (Number.isFinite(walletTotal) && parsed > walletTotal + 1e-8) {
            setError(`O PnL não pode ser maior que o total da carteira (${formatUsd(walletTotal)} USDC).`);
            return;
        }

        setSaving(true);
        setError('');
        updateUserPnl(userId, parsed)
            .then(result => {
                setSaving(false);
                closeRef.current?.click();
                if (onSubmit) onSubmit(result);
            })
            .catch(err => {
                setSaving(false);
                setError(apiError(err));
            });
    }

    return (
        <div className="modal fade" id="modalPnl" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title">Corrigir PnL</p>
                        <button ref={closeRef} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>
                    <div className="modal-body">
                        <p className="text-muted small mb-3">
                            Não mexe no saldo da carteira. Só ajusta o capital depositado para o PnL exibido ficar correto.
                        </p>
                        <p className="text-muted small mb-3">
                            Carteira: {formatUsd(walletTotal)} USDC
                            {' · '}
                            PnL atual: {formatUsd(currentPnl, true)} USDC
                            {Number.isFinite(deposited)
                                ? ` · Capital depositado: ${formatUsd(deposited)} USDC`
                                : ''}
                        </p>
                        <div className="mb-3">
                            <label htmlFor="correctPnl" className="form-label">PnL correto (USDC)</label>
                            <input
                                id="correctPnl"
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={pnl}
                                onChange={event => setPnl(event.target.value)}
                                placeholder="0.00"
                            />
                            {previewDeposited != null
                                ? (
                                    <small className="text-muted d-block mt-1">
                                        Capital depositado passará a {formatUsd(previewDeposited)} USDC.
                                    </small>
                                )
                                : <></>}
                        </div>
                    </div>
                    <div className="modal-footer">
                        {error
                            ? <div className="alert alert-danger mt-1 col-9 py-1 mb-0">{error}</div>
                            : <></>}
                        <button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={saving || !userId}>
                            {saving ? 'Salvando...' : 'Salvar PnL'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PnlModal;
